<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, reactive, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { FolderTree, Pencil, Plus, RefreshCw, Trash2 } from 'lucide-vue-next'
import { formatApiError, useApiClient } from '@yuksalish/api-client'
import { useAuthStore } from '@yuksalish/auth'
import {
  createProductGroup,
  deleteProductGroup,
  listProductGroups,
  updateProductGroup,
} from '../productService'
import type { ProductGroupOption } from '../types'

const client = useApiClient()
const auth = useAuthStore()
const { t } = useI18n()
const groups = ref<ProductGroupOption[]>([])
const loading = ref(false)
const saving = ref(false)
const deletingId = ref<number | null>(null)
const errorMessage = ref('')
const formError = ref('')
const search = ref('')
const editingId = ref<number | null>(null)
const drawerOpen = ref(false)
const originalParentId = ref<number | null>(null)
const form = reactive({
  name: '',
  code: '',
  parent_id: '',
})

const canCreate = computed(() => auth.user?.permissions.includes('products.create') ?? false)
const canUpdate = computed(() => auth.user?.permissions.includes('products.update') ?? false)
const canDelete = computed(() => auth.user?.permissions.includes('products.delete') ?? false)
const canSave = computed(() => (editingId.value == null ? canCreate.value : canUpdate.value))
const permissionHint = computed(() => t('engineering.productGroups.permissionHint'))
const isEditing = computed(() => editingId.value != null)

const groupById = computed(() => {
  const map = new Map<number, ProductGroupOption>()
  for (const group of groups.value) map.set(group.id, group)
  return map
})

/** Flattened hierarchy: every parent directly followed by its children. */
const orderedGroups = computed(() => {
  const query = search.value.trim().toLocaleLowerCase()
  const visible = query
    ? groups.value.filter((group) =>
        [group.name, group.code].some((value) =>
          String(value ?? '').toLocaleLowerCase().includes(query),
        ),
      )
    : groups.value
  const known = new Set(visible.map((group) => group.id))
  const children = new Map<number | null, ProductGroupOption[]>()
  for (const group of visible) {
    // A parent hidden by the search must not make its child unreachable.
    const parentKey = group.parent_id !== null && known.has(group.parent_id) ? group.parent_id : null
    const bucket = children.get(parentKey) ?? []
    bucket.push(group)
    children.set(parentKey, bucket)
  }
  for (const bucket of children.values()) {
    bucket.sort((left, right) => left.name.localeCompare(right.name))
  }
  const rows: Array<ProductGroupOption & { depth: number }> = []
  const visited = new Set<number>()
  const walk = (parentId: number | null, depth: number) => {
    for (const group of children.get(parentId) ?? []) {
      if (visited.has(group.id)) continue
      visited.add(group.id)
      rows.push({ ...group, depth })
      walk(group.id, depth + 1)
    }
  }
  walk(null, 0)
  // Defensive: a cycle (only possible with bad data) would leave rows out.
  for (const group of visible) {
    if (!visited.has(group.id)) rows.push({ ...group, depth: 0 })
  }
  return rows
})

function descendantsOf(id: number): Set<number> {
  const result = new Set<number>()
  const walk = (parentId: number) => {
    for (const group of groups.value) {
      if (group.parent_id === parentId && !result.has(group.id)) {
        result.add(group.id)
        walk(group.id)
      }
    }
  }
  walk(id)
  return result
}

/** Parent choices: no self, no descendant (the API only blocks self-parenting). */
const parentOptions = computed(() => {
  const blocked = new Set<number>()
  if (editingId.value != null) {
    blocked.add(editingId.value)
    for (const id of descendantsOf(editingId.value)) blocked.add(id)
  }
  return groups.value
    .filter((group) => !blocked.has(group.id))
    .sort((left, right) => left.name.localeCompare(right.name))
})

function parentName(parentId: number | null): string {
  if (parentId === null) return t('engineering.productGroups.parentNone')
  return groupById.value.get(parentId)?.name ?? t('engineering.productGroups.parentMissing')
}

function resetForm(): void {
  editingId.value = null
  originalParentId.value = null
  form.name = ''
  form.code = ''
  form.parent_id = ''
  formError.value = ''
}

function openCreate(): void {
  resetForm()
  drawerOpen.value = true
}

function closeDrawer(): void {
  if (saving.value) return
  drawerOpen.value = false
  resetForm()
}

function startEdit(item: ProductGroupOption): void {
  editingId.value = item.id
  originalParentId.value = item.parent_id
  form.name = item.name
  form.code = item.code
  form.parent_id = item.parent_id !== null ? String(item.parent_id) : ''
  formError.value = ''
  drawerOpen.value = true
}

async function loadGroups(): Promise<void> {
  loading.value = true
  errorMessage.value = ''
  try {
    groups.value = await listProductGroups(client)
  } catch (error: unknown) {
    groups.value = []
    errorMessage.value = formatApiError(error)
  } finally {
    loading.value = false
  }
}

async function submit(): Promise<void> {
  if (!canSave.value) return
  formError.value = ''
  const parentId = form.parent_id ? Number(form.parent_id) : null
  // `ProductGroupRepository::update()` ignores a null parent, so clearing the
  // parent would silently do nothing — refuse instead of pretending it worked.
  if (editingId.value != null && parentId === null && originalParentId.value !== null) {
    formError.value = t('engineering.productGroups.parentClearUnsupported')
    return
  }
  saving.value = true
  try {
    const payload = {
      name: form.name.trim(),
      code: form.code.trim(),
      ...(parentId !== null ? { parent_id: parentId } : {}),
    }
    if (editingId.value == null) {
      await createProductGroup(client, payload)
    } else {
      await updateProductGroup(client, editingId.value, payload)
    }
    await loadGroups()
    drawerOpen.value = false
    resetForm()
  } catch (error: unknown) {
    formError.value = formatApiError(error)
  } finally {
    saving.value = false
  }
}

async function removeGroup(item: ProductGroupOption): Promise<void> {
  if (!canDelete.value) return
  if (groups.value.some((group) => group.parent_id === item.id)) {
    errorMessage.value = t('engineering.productGroups.deleteHasChildren', { name: item.name })
    return
  }
  if (!window.confirm(t('engineering.productGroups.confirmDelete', { name: item.name }))) return
  deletingId.value = item.id
  errorMessage.value = ''
  try {
    await deleteProductGroup(client, item.id)
    if (editingId.value === item.id) closeDrawer()
    await loadGroups()
  } catch (error: unknown) {
    errorMessage.value = formatApiError(error)
  } finally {
    deletingId.value = null
  }
}

function onKeydown(event: KeyboardEvent): void {
  if (event.key === 'Escape' && drawerOpen.value) closeDrawer()
}

onMounted(() => {
  loadGroups()
  window.addEventListener('keydown', onKeydown)
})
onBeforeUnmount(() => window.removeEventListener('keydown', onKeydown))
</script>

<template>
  <div class="page-heading">
    <div>
      <p class="eyebrow">{{ t('engineering.productGroups.eyebrow') }}</p>
      <h2>{{ t('engineering.productGroups.title') }}</h2>
      <p>{{ t('engineering.productGroups.description') }}</p>
    </div>
    <div class="heading-actions">
      <div class="summary-tile">
        <FolderTree :size="18" aria-hidden="true" />
        <div>
          <strong>{{ groups.length }}</strong>
          <span>{{ t('engineering.productGroups.count') }}</span>
        </div>
      </div>
      <button class="primary-button" type="button" :disabled="!canCreate" :title="!canCreate ? permissionHint : undefined" @click="openCreate">
        <Plus :size="16" aria-hidden="true" />
        {{ t('engineering.productGroups.create') }}
      </button>
    </div>
  </div>

  <section class="workspace">
    <section class="list-card">
      <div class="list-toolbar">
        <label class="search-field">
          <span>{{ t('engineering.productGroups.search') }}</span>
          <input v-model="search" type="search" :placeholder="t('engineering.productGroups.searchPlaceholder')">
        </label>
        <button class="ghost-button" type="button" :disabled="loading" @click="loadGroups">
          <RefreshCw :size="16" aria-hidden="true" />
          {{ t('engineering.action.retry') }}
        </button>
      </div>

      <div v-if="errorMessage" class="message message--error" role="alert">{{ errorMessage }}</div>
      <div v-if="loading" class="table-state">{{ t('engineering.productGroups.loading') }}</div>
      <div v-else-if="!orderedGroups.length" class="table-state">{{ t('engineering.productGroups.empty') }}</div>
      <table v-else>
        <thead>
          <tr>
            <th>{{ t('engineering.productGroups.name') }}</th>
            <th>{{ t('engineering.productGroups.code') }}</th>
            <th>{{ t('engineering.productGroups.parent') }}</th>
            <th>{{ t('engineering.catalog.actions') }}</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="item in orderedGroups" :key="item.id">
            <td>
              <span class="tree-label" :style="{ paddingLeft: `${item.depth * 18}px` }">
                <span v-if="item.depth" class="tree-branch" aria-hidden="true">└</span>
                <strong>{{ item.name }}</strong>
              </span>
              <small>#{{ item.id }}</small>
            </td>
            <td>{{ item.code }}</td>
            <td>{{ parentName(item.parent_id) }}</td>
            <td class="row-actions">
              <button
                class="icon-button"
                type="button"
                :disabled="!canUpdate"
                :title="!canUpdate ? permissionHint : item.name"
                :aria-label="t('engineering.productGroups.edit')"
                @click="startEdit(item)"
              >
                <Pencil :size="15" aria-hidden="true" />
              </button>
              <button
                class="icon-button icon-button--danger"
                type="button"
                :disabled="!canDelete || deletingId === item.id"
                :title="!canDelete ? permissionHint : item.name"
                :aria-label="t('engineering.productGroups.remove')"
                @click="removeGroup(item)"
              >
                <Trash2 :size="15" aria-hidden="true" />
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </section>
  </section>

  <Teleport to="body">
    <div v-if="drawerOpen" class="drawer-layer" role="presentation">
      <button class="drawer-backdrop" type="button" :aria-label="t('engineering.action.cancel')" @click="closeDrawer" />
      <aside class="drawer" role="dialog" aria-modal="true" :aria-labelledby="'product-group-drawer-title'">
        <form class="drawer-form" @submit.prevent="submit">
          <div class="section-heading drawer-heading">
            <div>
              <p class="eyebrow">{{ isEditing ? t('engineering.productGroups.editEyebrow') : t('engineering.productGroups.createEyebrow') }}</p>
              <h3 id="product-group-drawer-title">{{ isEditing ? t('engineering.productGroups.editTitle') : t('engineering.productGroups.createTitle') }}</h3>
            </div>
            <button class="drawer-close" type="button" :disabled="saving" :aria-label="t('engineering.action.cancel')" @click="closeDrawer">×</button>
          </div>

          <div class="drawer-body">
            <p v-if="formError" class="message message--error" role="alert">{{ formError }}</p>
            <p v-if="!canSave" class="message message--hint">{{ permissionHint }}</p>
            <div class="form-grid">
              <label><span>{{ t('engineering.productGroups.name') }}</span><input v-model="form.name" :disabled="saving || !canSave" type="text" autofocus></label>
              <label><span>{{ t('engineering.productGroups.code') }}</span><input v-model="form.code" :disabled="saving || !canSave" type="text"></label>
              <label>
                <span>{{ t('engineering.productGroups.parent') }}</span>
                <select v-model="form.parent_id" :disabled="saving || !canSave">
                  <option value="">{{ t('engineering.productGroups.parentNone') }}</option>
                  <option v-for="group in parentOptions" :key="group.id" :value="String(group.id)">{{ group.name }} ({{ group.code }})</option>
                </select>
                <small class="field-hint">{{ t('engineering.productGroups.parentHint') }}</small>
              </label>
            </div>
          </div>

          <div class="drawer-footer">
            <button class="ghost-button" type="button" :disabled="saving" @click="closeDrawer">{{ t('engineering.action.cancel') }}</button>
            <button class="primary-button" type="submit" :disabled="saving || !canSave || !form.name.trim() || !form.code.trim()" :title="!canSave ? permissionHint : undefined">
              <Plus v-if="!isEditing" :size="16" aria-hidden="true" /><Pencil v-else :size="16" aria-hidden="true" />
              {{ saving ? t('engineering.productGroups.saving') : (isEditing ? t('engineering.productGroups.saveChanges') : t('engineering.productGroups.create')) }}
            </button>
          </div>
        </form>
      </aside>
    </div>
  </Teleport>
</template>

<style scoped>
.page-heading,.section-heading,.list-toolbar,.form-actions,.row-actions,.heading-actions,.drawer-footer{display:flex;gap:12px;align-items:center;justify-content:space-between}
.page-heading{margin-bottom:24px}
.workspace,.list-card{width:100%;min-width:0}
.list-card{padding:18px;border:1px solid #d7dfdc;border-radius:8px;background:#fff}
.eyebrow{margin:0 0 4px;color:#66726e;font-size:12px;font-weight:700;text-transform:uppercase}
h2,h3,p{margin:0}
.page-heading>div:first-child p:last-child{margin-top:8px;color:#66726e}
.summary-tile{display:inline-flex;gap:12px;align-items:center;padding:12px 14px;border:1px solid #d7dfdc;border-radius:8px;background:#fff}
.summary-tile strong,td strong{display:block}
.summary-tile span,td small{color:#66726e;font-size:12px}
.form-grid{display:grid;gap:12px;margin-top:16px}
label{display:grid;gap:6px}
label span,th{color:#586660;font-size:12px;font-weight:700;text-transform:uppercase}
input,select,button{min-height:40px;padding:8px 10px;border:1px solid #adb9b4;border-radius:8px;background:#fff;font:inherit}
.field-hint{color:#7b8782;font-size:12px}
.primary-button,.ghost-button,.icon-button{display:inline-flex;gap:8px;align-items:center;justify-content:center;width:auto;font-weight:700;cursor:pointer}
.primary-button{border:0;color:#fff;background:#3568a8}
.ghost-button,.icon-button{color:#285b9a}
.icon-button--danger{color:#8b3025}
.form-actions{justify-content:flex-start;margin-top:16px}
.search-field{flex:1}
.search-field input{width:100%}
.message{margin-top:16px;padding:12px 14px;border-radius:8px}
.message--error{border:1px solid #d7a8a1;color:#852d22;background:#fff5f3}
.message--hint{border:1px solid #d7dfdc;color:#66726e;background:#f7faf9}
.list-card{overflow-x:auto}.list-card table{width:100%;min-width:640px;margin-top:12px;border-collapse:collapse}
th,td{padding:6px 10px;border-bottom:1px solid #e1e6e4;text-align:left;vertical-align:middle;font-size:13px}
th{background:#f3f6f5;font-size:11px}
td small{font-size:11px}
/* Compact icon-only row actions: 30px squares, no labels. */
td.row-actions{display:table-cell;width:1%;text-align:right;white-space:nowrap}
.icon-button{width:30px;min-width:30px;height:30px;min-height:30px;gap:0;padding:0;border-radius:6px}
.icon-button+.icon-button{margin-left:6px}
.icon-button--danger{border:0;color:#fff;background:#c0392b}
.icon-button:hover:not(:disabled){border-color:#285b9a;background:#f3f7fc}
.icon-button--danger:hover:not(:disabled){border:0;color:#fff;background:#a5301f}
.tree-label{display:inline-flex;align-items:center;gap:6px}
.tree-branch{color:#9aa6a1}
.table-state{display:grid;min-height:220px;color:#697671;place-items:center}
button:disabled{cursor:not-allowed;opacity:.55}
.drawer-layer{position:fixed;z-index:1200;inset:0;display:flex;justify-content:flex-end}.drawer-backdrop{position:absolute;inset:0;width:100%;min-height:0;padding:0;border:0;border-radius:0;background:rgb(18 30 27 / 46%);cursor:default}.drawer{position:relative;width:min(460px,100%);height:100%;overflow-x:hidden;overflow-y:auto;border-left:1px solid #d7dfdc;background:#fff;box-shadow:-12px 0 32px rgb(25 40 35 / 18%)}.drawer-form{display:flex;min-width:0;min-height:100%;flex-direction:column}.drawer-heading{position:sticky;z-index:1;top:0;padding:20px;border-bottom:1px solid #e1e6e4;background:#fff}.drawer-close{width:40px;padding:0;border-color:transparent;color:#586660;font-size:28px;font-weight:400;line-height:1}.drawer-body{min-width:0;flex:1;padding:20px}.drawer-body .form-grid,.drawer-body label{min-width:0}.drawer-body input,.drawer-body select{box-sizing:border-box;width:100%;max-width:100%}.drawer-body .message{margin-top:0;margin-bottom:16px}.drawer-footer{position:sticky;bottom:0;justify-content:flex-end;padding:16px 20px;border-top:1px solid #e1e6e4;background:#fff}
@media (max-width:640px){.page-heading,.list-toolbar{align-items:stretch;flex-direction:column}.heading-actions{justify-content:space-between}.row-actions{white-space:nowrap}.drawer{width:100%}.drawer-heading,.drawer-body{padding:16px}.drawer-footer{padding:12px 16px}}
</style>
